import { Component, computed, inject, Input, OnDestroy, OnInit, signal, Signal, WritableSignal } from "@angular/core";
import { ButtonComponent, IconComponent } from "libs/shared";
import { FormsModule } from "@angular/forms";
import { Sector, SectorManualActionsPermission, SectorMode, SectorState, SectorType } from "libs/domain";
import { SectorService } from "libs/features";
import { SchemaMembershipStateService } from "libs/core";
import { ToastService } from "libs/core/toast/toast.service";
import { ModalController, IonHeader, IonContent, NavController } from "@ionic/angular/standalone";
import { interval, Subject, Subscription, take, takeUntil } from "rxjs";

@Component({
  selector: 'lib-sector-manual-actions',
  imports: [IonContent, IonHeader, FormsModule, ButtonComponent, IconComponent],
  templateUrl: "./sector-manual-actions.component.html",
})
export class SectorManualActions implements OnInit, OnDestroy{

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.startAutoRefresh();
  }
  
  modalCtrl = inject(ModalController);
  toastService = inject(ToastService);
  sectorService = inject(SectorService);
  navController = inject(NavController);
  schemaStateService = inject(SchemaMembershipStateService);
  
  @Input({required: true}) sector!: WritableSignal<Sector>;
  
  //Refresh de 5s 
  refreshSubscription?: Subscription;
  refreshInterval = 5000;
  
  manualStartDisabled: boolean = false;
  manualStopDisabled: boolean = false;
  automaticDisabled: boolean = false;

  title = computed(() => {
    switch(this.sector().type) {
      case SectorType.ISOLATED:
      case SectorType.SELF_SECTORIZED:
      case SectorType.SECTORIZED:
        return "Toma " + this.sector().name
      case SectorType.SECTOR:
        return "Sector " + this.sector().name
    }
  })

  startAutoRefresh() {
    this.refreshSubscription?.unsubscribe();

    this.refreshSubscription = interval(this.refreshInterval)
    .pipe(
      take(5),
      takeUntil(this.destroy$)
    )
    .subscribe(() => {
      this.refreshSector();
    }); 

  }

  refreshSector() {
    this.sectorService.getSector(this.schemaStateService.schema()!.schema_id, this.sector().id)
      .subscribe(updatedSector => {
        this.sector.set(updatedSector); 
      });
  }

  hasPermission = computed(() => {
    return [SectorManualActionsPermission.BASIC, SectorManualActionsPermission.FULL].includes(this.sector().manualActionsPermission);
  });
  
  showNoPermissionMessage = computed(() => {
    if (this.hasPermission()) return null;
    return this.sector().type === SectorType.SECTOR
      ? `No tienes permisos para realizar acciones manuales en este sector.`
      : `No tienes permisos para realizar acciones manuales en esta toma.`;
  }); 

  modeText = computed( () => {
    switch(this.sector().mode) {
      case SectorMode.AUTOMATIC:
        return "Automático";
      case SectorMode.MANUAL:
        return "Manual";
    }
  })

  showManualStop = computed( () => {
    let hasPermission = false;
    switch(this.sector().manualActionsPermission) {
      case SectorManualActionsPermission.NONE:
        hasPermission = false;
        break;
      case SectorManualActionsPermission.BASIC:
      case SectorManualActionsPermission.FULL:
        hasPermission = true;
        break;
    }
    
    let isAutomatic = this.sector().mode === SectorMode.AUTOMATIC;

    let isCompatible = false;
    switch(this.sector().state){
      case SectorState.DISCONNECTED:
      case SectorState.NO_IRRIG:
      case SectorState.LEAK:
      case SectorState.UNKNOWN:
        isCompatible = false;
        break;
      case SectorState.START_IRRIG:
      case SectorState.IRRIG:
      case SectorState.INCIDENCE:
      case SectorState.MALFUNCTION:
      case SectorState.WAIT_WATER:
      case SectorState.IRRIGATION_ALLOCATION:
        isCompatible = true;
        break;
    }
    return hasPermission && (isCompatible || isAutomatic);
  })

  showManualStart = computed( () => {
    let hasPermission = false;
    switch(this.sector().manualActionsPermission) {
      case SectorManualActionsPermission.NONE:
      case SectorManualActionsPermission.BASIC:
        hasPermission = false;
        break;
      case SectorManualActionsPermission.FULL:
        hasPermission = true;
        break;
    }

    let isAutomatic = this.sector().mode === SectorMode.AUTOMATIC;

    let isCompatible = false;
    switch(this.sector().state){
      case SectorState.DISCONNECTED:
      case SectorState.NO_IRRIG:
      case SectorState.LEAK:
      case SectorState.UNKNOWN:
        isCompatible = true;
        break;
      case SectorState.START_IRRIG:
      case SectorState.IRRIG:
      case SectorState.INCIDENCE:
      case SectorState.MALFUNCTION:
      case SectorState.WAIT_WATER:
      case SectorState.IRRIGATION_ALLOCATION:
        isCompatible = false;
        break;
    }
    return hasPermission && (isCompatible || isAutomatic);
  })

  showAutomatic = computed( () => {
    let hasPermission = false;
    switch(this.sector().manualActionsPermission) {
      case SectorManualActionsPermission.NONE:
        hasPermission = false;
        break;
      case SectorManualActionsPermission.BASIC:
      case SectorManualActionsPermission.FULL:
        hasPermission = true;
        break;
    }
    let isCompatible = this.sector().mode !== SectorMode.AUTOMATIC;
    return hasPermission && isCompatible;
  })
  
  onModalCloseHandler(){
    this.modalCtrl.dismiss(this.sector())
  }

  disableButton(action: 'MANUAL_START' | 'MANUAL_STOP' | 'AUTO'){
    switch(action){
      case "AUTO":
        this.automaticDisabled = true;
        break;
      case "MANUAL_START":
        this.manualStartDisabled = true;
        break;
      case "MANUAL_STOP":
        this.manualStopDisabled = true;
        break;
    }
    setTimeout(() => {
      switch(action){
        case "AUTO":
          this.automaticDisabled = false;
          break;
        case "MANUAL_START":
          this.manualStartDisabled = false;
          break;
        case "MANUAL_STOP":
          this.manualStopDisabled = false;
          break;
      }
    }, 5000)
  }

  
  sendAction(action: 'MANUAL_START' | 'MANUAL_STOP' | 'AUTO'){
    this.disableButton(action);
    
    this.sectorService.sendManualAction(this.schemaStateService.schema()!.schema_id, this.sector().id, action)
    .subscribe({
      next: () => {
        let title;
        switch(action) {
          case "MANUAL_STOP":
            title = "¡Manual paro enviado correctamente!"
            break;
          case "MANUAL_START":
            title = "¡Manual marcha enviado correctamente!"
            break;
          case "AUTO":
            title = "¡Automático enviado correctamente!"
            break;
        }
        this.toastService.presentToast(
          'success', 
          title, 
          'La acción se ha enviado correctamente. Recuerde que puede tardar unos minutos en aplicarse.', 
          'bottom'
        )
        this.startAutoRefresh()
      },
      error: () => {
        this.toastService.presentToast(
          'error',
          "¡Ha habido un error enviando la acción manual!",
          'Inténtelo de nuevo. Si el problema persiste, póngase en contacto con nosotros.', 
          'bottom'
        )
      }
    })
  }

  closeModal(){
    this.modalCtrl.dismiss()
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}